import React, { useCallback, useContext } from 'react';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { getIsRfiStatusClosed } from '../utils';
import dataRoomApi from '../dataRoomApi';
import {
  DATA_ROOM_RFI_DISPLAY_STATUS,
  DataRoomRFI,
} from '../models/DataRoomRfi';
import { useTranslation } from 'react-i18next';
import RfiContext from '../providers/Rfi/Rfi.context';
import confirm from 'modules/confirm';
import { getRfiDisplayStatusAfterReopen } from '../subrouters/RFI/pages/RfiList/components/RfiRequestsTable/utils';

const useRfiChangeStatus = (
  rfi: DataRoomRFI,
  onRfiUpdate: (rfi: DataRoomRFI) => void,
) => {
  const { _id, status } = rfi || {};

  const { t } = useTranslation();

  const { showActionUndo, refetchRfiStatusCounts } = useContext(RfiContext);

  const applyChangeStatus = useCallback(
    async (isClosed: boolean) => {
      const { data } = await dataRoomApi.rfi[
        isClosed ? 'reopenRfi' : 'closeRfi'
      ](_id);

      onRfiUpdate({
        ...data,
        displayStatus: isClosed
          ? getRfiDisplayStatusAfterReopen(rfi)
          : DATA_ROOM_RFI_DISPLAY_STATUS.CLOSED,
      });

      refetchRfiStatusCounts();
    },
    [_id, onRfiUpdate, refetchRfiStatusCounts, rfi],
  );

  const [changeStatus, loading] = useCallbackAsync(async () => {
    const isClosed = getIsRfiStatusClosed(status);

    if (
      !isClosed &&
      !(await confirm({
        title: t('closeRequestConfirmTitle'),
        content: (
          <>
            <p>{t('closeRequestConfirmDescription')}</p>
            <p>{t('closeRequestConfirmDescriptionSecondary')}</p>
          </>
        ),
        submitBtnText: t('closeRequest'),
      }))
    )
      return;

    await applyChangeStatus(isClosed);

    showActionUndo(t(isClosed ? 'rfiReopened' : 'rfiClosed'), () =>
      applyChangeStatus(!isClosed),
    );
  });

  return { changeStatus, loading };
};

export default useRfiChangeStatus;
